/* Notification Center — client store (Zustand), DB-backed for RULES.
 *
 * Rules + categories persist to dbo.AI_NotificationRule / AI_NotificationCategory
 * via ./api (NotificationRulesController): the store HYDRATES from the API on
 * load() and WRITE-THROUGHS every rule/category mutation, so new/edited rules
 * survive an API restart.
 *
 * NO static/seed RULE data (2026-09-24, RTCFR): `categories` starts EMPTY and
 * `loaded` is false until the API responds — the UI shows a loading state, never
 * the old NC_CATEGORIES mock, so no fake rules ever flash before real data. On a
 * load FAILURE we keep whatever we already had (initially none) and set `error`;
 * we never fall back to seed rules. Recipient groups / templates / deliveries are
 * still seed-only demo for their SEPARATE screens (not rules, not API-backed). */
import { create } from 'zustand';
import {
  type NcCategory, type NcRule, type NcRecipientGroup, type NcTemplate, type NcDelivery,
} from './data';
import * as ncApi from './api';
import { errorDlg } from '../../components/confirm/dialogBus';
import { effectiveAck } from './deliveryMethod';

/** Surface a persistence failure (the axios interceptor does not dialog generic
 *  errors), so a save that did not reach the DB is never silently lost. */
function saveErr(): void {
  void errorDlg('Could not save the change to the server — it may not persist after reload. Please retry.');
}
/** Persist one rule by Code — resolves its category from the current tree.
 *  Returns the write-through promise so callers can refresh derived data. */
function persistRule(cats: NcCategory[], code: string): Promise<void> {
  for (const c of cats) {
    const r = c.rules.find((x) => x.code === code);
    if (r) return ncApi.upsertRule(c.code, r).catch(saveErr);
  }
  return Promise.resolve();
}

export interface NcSummary {
  totalRules: number; activeRules: number; disabledRules: number;
  requireAck: number; outbound: number; needRecipients: number;
  categories: number; activeCategories: number;
  recipientGroups: number; templates: number;
}

/** Real Delivery Log counts (from the DB) — never mock. */
export interface NcDeliveryStats { total: number; failed: number; last24h: number; failed24h: number; }
export const EMPTY_DELIVERY_STATS: NcDeliveryStats = { total: 0, failed: 0, last24h: 0, failed24h: 0 };

export function summaryOf(categories: NcCategory[], recipients: NcRecipientGroup[], templates: NcTemplate[]): NcSummary {
  const rules = categories.flatMap((c) => c.rules);
  return {
    totalRules: rules.length,
    activeRules: rules.filter((r) => r.enabled).length,
    disabledRules: rules.filter((r) => !r.enabled).length,
    // Only In-App alerts can be acknowledged — a rule with In-App off requires nothing.
    requireAck: rules.filter((r) => effectiveAck(r.notifyInApp, r.ack)).length,
    outbound: rules.filter((r) => r.outbound).length,
    // Rules that can't send yet because no recipient is set (2026-09-21: derived
    // from the data, not a hardcoded demo number — so it reads 0 when empty).
    needRecipients: rules.filter((r) => {
      const rc = (r.recipients ?? '').trim();
      return rc === '' || rc === '—';
    }).length,
    categories: categories.length,
    activeCategories: categories.filter((c) => c.active).length,
    recipientGroups: recipients.length,
    templates: templates.length,
  };
}

interface NcStore {
  categories: NcCategory[];
  recipients: NcRecipientGroup[];
  templates: NcTemplate[];
  /** Recent Delivery Log rows (newest first) for the Overview mini-list — real DB
   *  data, hydrated in load(). The full Delivery Log page pages the DB itself. */
  deliveries: NcDelivery[];
  /** Real Delivery Log counts (from the DB) for the Overview KPIs. */
  deliveryStats: NcDeliveryStats;
  /** True once the categories+rules tree has been hydrated from the API at least
   *  once. The Rules/Overview/Categories views show a loading state until then —
   *  never mock rules. */
  loaded: boolean;
  /** True when the last hydrate FAILED (API unreachable/error). The views show an
   *  error state instead of fake data; a Refresh clears it on success. */
  error: boolean;

  /** Hydrate categories+rules from the DB. On failure keep the current data
   *  (initially none) and flag `error` — NEVER seed mock rules. */
  load: () => Promise<void>;

  // rules — each mutation write-throughs to the API
  /** Create a rule in the DB (the server mints its Code) and add it to the list. Returns the saved rule,
   *  or null when the save failed (the error dialog has been shown). */
  createRule: (categoryName: string, rule: NcRule) => Promise<NcRule | null>;
  deleteRuleCodes: (codes: string[]) => void;
  toggleRule: (code: string) => void;
  /** Persist a rule's monitored-target selection (enabled Permit Library keys). */
  updateRuleTargets: (code: string, targetKeys: string[]) => void;
  /** Merge a partial into a rule (name / severity / recipients / config / enabled
   *  / targetKeys) and write-through to the API. Used by the Rule Detail Save. */
  patchRule: (code: string, patch: Partial<NcRule>) => void;
  /** Re-fetch just the recipient groups from the DB (their computed Used-by count
   *  changes when a rule's group assignment changes). */
  refreshGroups: () => void;
  /** Re-read the categories/rules tree (e.g. after a group rename, so each rule's
   *  Recipients text shows the group's CURRENT name — resolved by id server-side). */
  refreshRules: () => void;

  // categories
  /** Create a category. NOT optimistic: its Category Code is minted by the server,
   *  so the row appears (with that code) once the save lands. Resolves true on
   *  success; a failure shows the shared save-error dialog and resolves false. */
  addCategory: (cat: NcCategory) => Promise<boolean>;
  updateCategory: (code: number, patch: Partial<NcCategory>) => void;
  deleteCategoryCodes: (codes: number[]) => void;

  // recipient groups (seed-only demo)
  addRecipient: (group: NcRecipientGroup) => void;
  updateRecipient: (name: string, patch: Partial<NcRecipientGroup>) => void;
  deleteRecipientNames: (names: string[]) => void;

  // email templates (seed-only demo)
  addTemplate: (tpl: NcTemplate) => void;
  updateTemplate: (name: string, patch: Partial<NcTemplate>) => void;
  deleteTemplateNames: (names: string[]) => void;

  reset: () => void;
}

export const useNcStore = create<NcStore>((set, get) => ({
  // RULES + TEMPLATES + RECIPIENT GROUPS start EMPTY — no mock rows before the API
  // responds (loaded gates the UI). All hydrate from the DB in load().
  categories: [],
  recipients: [],
  templates: [],
  deliveries: [],
  deliveryStats: EMPTY_DELIVERY_STATS,
  loaded: false,
  error: false,

  load: async () => {
    try {
      // Categories/rules tree + templates + recipient groups + recent deliveries,
      // all DB-backed. The delivery page shows the recent rows + real counts.
      const [cats, tpls, groups, deliv] = await Promise.all([
        ncApi.getAll(), ncApi.getTemplates(), ncApi.getRecipientGroups(), ncApi.getDeliveries(1, 50),
      ]);
      set({
        categories: cats, templates: tpls, recipients: groups,
        deliveries: deliv.items,
        deliveryStats: { total: deliv.total, failed: deliv.failed, last24h: deliv.last24h, failed24h: deliv.failed24h },
        loaded: true, error: false,
      });
    } catch {
      // API unreachable — DO NOT seed mock rows. Keep current data (initially
      // none) and flag the error so the UI shows an error state, not fake rows.
      set({ loaded: true, error: true });
    }
  },

  createRule: async (categoryName, rule) => {
    const cat = get().categories.find((c) => c.name === categoryName);
    if (!cat) { saveErr(); return null; }
    const nextSort = cat.rules.length ? Math.max(...cat.rules.map((r) => r.sortOrder ?? 0)) + 10 : 0;
    try {
      // The server mints the code, so the rule is added to the list only once it exists.
      const code = await ncApi.createRule(cat.code, { ...rule, sortOrder: nextSort });
      const saved: NcRule = { ...rule, code, sortOrder: nextSort };
      set((s) => ({
        categories: s.categories.map((c) => (c.code === cat.code ? { ...c, rules: [...c.rules, saved] } : c)),
      }));
      get().refreshGroups();
      return saved;
    } catch {
      saveErr();
      return null;
    }
  },
  deleteRuleCodes: (codes) => {
    set((s) => ({
      categories: s.categories.map((c) => ({ ...c, rules: c.rules.filter((r) => !codes.includes(r.code)) })),
    }));
    ncApi.deleteRules(codes).then(() => get().refreshGroups()).catch(saveErr);
  },
  toggleRule: (code) => {
    set((s) => ({
      categories: s.categories.map((c) => ({
        ...c, rules: c.rules.map((r) => (r.code === code ? { ...r, enabled: !r.enabled } : r)),
      })),
    }));
    persistRule(get().categories, code);
  },
  updateRuleTargets: (code, targetKeys) => {
    const label = `${targetKeys.length} target${targetKeys.length === 1 ? '' : 's'}`;
    set((s) => ({
      categories: s.categories.map((c) => ({
        ...c, rules: c.rules.map((r) =>
          (r.code === code ? { ...r, targetKeys, targets: targetKeys.length ? label : undefined } : r)),
      })),
    }));
    persistRule(get().categories, code);
  },
  patchRule: (code, patch) => {
    set((s) => ({
      categories: s.categories.map((c) => ({
        ...c, rules: c.rules.map((r) => (r.code === code ? { ...r, ...patch } : r)),
      })),
    }));
    // Recipient-group assignment can change here (Rule Detail Save) → refresh the
    // groups' Used-by counts once the write-through lands.
    void persistRule(get().categories, code).then(() => get().refreshGroups());
  },
  refreshGroups: () => {
    ncApi.getRecipientGroups().then((groups) => set({ recipients: groups })).catch(() => { /* keep current on failure */ });
  },
  refreshRules: () => {
    ncApi.getAll().then((categories) => set({ categories })).catch(() => { /* keep current on failure */ });
  },

  addCategory: async (cat) => {
    try {
      const saved = await ncApi.upsertCategory({ ...cat, code: 0 });
      set((s) => ({
        categories: [...s.categories, { ...cat, code: saved.code, codeLabel: saved.codeLabel, rules: [] }]
          .sort((a, b) => a.order - b.order || a.name.localeCompare(b.name)),
      }));
      return true;
    } catch {
      saveErr();
      return false;
    }
  },
  updateCategory: (code, patch) => {
    set((s) => ({ categories: s.categories.map((c) => (c.code === code ? { ...c, ...patch } : c)) }));
    const updated = get().categories.find((c) => c.code === code);
    if (updated) ncApi.upsertCategory(updated).catch(saveErr);
  },
  deleteCategoryCodes: (codes) => {
    set((s) => ({ categories: s.categories.filter((c) => !codes.includes(c.code)) }));
    ncApi.deleteCategories(codes).catch(saveErr);
  },

  addRecipient: (group) => {
    set((s) => ({ recipients: [...s.recipients, group] }));
    // Stamp the new group's stable id as soon as the save returns it, so a rule
    // can link to it (rules select groups by id, never by name).
    ncApi.upsertRecipientGroup(group.name, group)
      .then((id) => set((s) => ({ recipients: s.recipients.map((r) => (r.name === group.name && !r.id ? { ...r, id } : r)) })))
      .catch(saveErr);
  },
  updateRecipient: (name, patch) => {
    const old = get().recipients.find((r) => r.name === name);
    if (!old) return;
    // The id is the identity and is never patched: a rename is an in-place update
    // of the SAME group, so every rule linked to it keeps the link.
    const merged: NcRecipientGroup = { ...old, ...patch, id: old.id };
    set((s) => ({ recipients: s.recipients.map((r) => (r.name === name ? merged : r)) }));
    ncApi.upsertRecipientGroup(name, merged)
      .then(() => { if (merged.name !== name) get().refreshRules(); })
      .catch(saveErr);
  },
  deleteRecipientNames: (names) => {
    set((s) => ({ recipients: s.recipients.filter((r) => !names.includes(r.name)) }));
    ncApi.deleteRecipientGroups(names).then(() => get().refreshRules()).catch(saveErr);
  },

  addTemplate: (tpl) => {
    const nextSort = get().templates.length
      ? Math.max(...get().templates.map((t) => t.sortOrder ?? 0)) + 10
      : 10;
    const saved: NcTemplate = { ...tpl, sortOrder: nextSort };
    set((s) => ({ templates: [...s.templates, saved] }));
    // Stamp the new template's stable id as soon as the save returns it, so a
    // rule can link to it (rules reference templates by id, never by name).
    ncApi.upsertTemplate(saved.name, saved, nextSort)
      .then((id) => set((s) => ({ templates: s.templates.map((t) => (t.name === saved.name && !t.id ? { ...t, id } : t)) })))
      .catch(saveErr);
  },
  updateTemplate: (name, patch) => {
    const old = get().templates.find((t) => t.name === name);
    if (!old) return;
    // The id is the identity and is never patched: a rename is an in-place update
    // of the SAME template, so every rule linked via EmailTemplateId keeps it.
    const merged: NcTemplate = { ...old, ...patch, id: old.id };
    set((s) => ({ templates: s.templates.map((t) => (t.name === name ? merged : t)) }));
    ncApi.upsertTemplate(name, merged, merged.sortOrder ?? 0)
      .then(() => { if (merged.name !== name) get().refreshRules(); })
      .catch(saveErr);
  },
  deleteTemplateNames: (names) => {
    set((s) => ({ templates: s.templates.filter((t) => !names.includes(t.name)) }));
    ncApi.deleteTemplates(names).catch(saveErr);
  },

  reset: () => set({
    categories: [],            // no mock rules — reload from the API
    recipients: [],            // no mock groups — reload from the API
    templates: [],             // no mock templates — reload from the API
    deliveries: [],            // no mock deliveries — reload from the API
    deliveryStats: EMPTY_DELIVERY_STATS,
    loaded: false,
    error: false,
  }),
}));
